import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter } from 'rxjs/operators';
import { UnitSchedule } from '../models/unit.model';
import { UnitService } from '../services/unit.service';
import { NotificationService } from '@shared/services/notification.service';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { DeleteConfirmDialogComponent, DeleteConfirmDialogData } from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';

const DAY_NAMES: Record<number, string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
};

export interface UnitSchedulesDialogData {
  unitId: string;
  schedules: UnitSchedule[];
}

@Component({
  selector: 'app-unit-schedules-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, MatTooltipModule],
  template: `
    <h2 mat-dialog-title>Horarios de servicios divinos</h2>
    <mat-dialog-content>
      <div *ngIf="schedules.length === 0" class="empty-state">No hay horarios configurados.</div>
      <div *ngFor="let schedule of schedules" class="schedule-row">
        <div>
          <strong>{{ getDayName(schedule.dayOfWeek) }} · {{ schedule.startTime }}</strong>
          <div *ngIf="schedule.notes" class="schedule-notes">{{ schedule.notes }}</div>
        </div>
        <button mat-icon-button type="button" (click)="deleteSchedule(schedule)" aria-label="Eliminar horario" matTooltip="Eliminar horario">
          <mat-icon>delete_outline</mat-icon>
        </button>
      </div>

      <form *ngIf="schedules.length < maxSchedules" [formGroup]="form" (ngSubmit)="addSchedule()" class="schedule-form">
        <mat-form-field appearance="outline">
          <mat-label>Día</mat-label>
          <mat-select formControlName="dayOfWeek" required>
            <mat-option *ngFor="let day of days" [value]="day.value">{{ day.label }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Hora</mat-label>
          <input matInput type="time" formControlName="startTime" required>
        </mat-form-field>
        <mat-form-field appearance="outline">
          <mat-label>Nota (opcional)</mat-label>
          <input matInput formControlName="notes" maxlength="200">
        </mat-form-field>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || isSaving">
          {{ isSaving ? 'Guardando…' : 'Agregar horario' }}
        </button>
      </form>
      <p *ngIf="schedules.length >= maxSchedules" class="limit-message">Se alcanzó el máximo de {{ maxSchedules }} horarios.</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()">Cerrar</button>
    </mat-dialog-actions>
  `,
  styles: [`
    mat-dialog-content { min-width: min(440px, calc(100vw - 64px)); }
    .schedule-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 0; border-bottom: 1px solid #e5e7eb; }
    .schedule-notes, .empty-state, .limit-message { color: #667085; }
    .schedule-notes { margin-top: 2px; font-size: 13px; }
    .schedule-form { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 12px; margin-top: 18px; }
    .schedule-form mat-form-field { width: 100%; }
    .schedule-form mat-form-field:last-of-type { grid-column: 1 / -1; }
    .schedule-form button { justify-self: start; }
    .limit-message { margin: 16px 0 0; }
    @media (max-width: 520px) { mat-dialog-content { min-width: 0; } }
  `],
})
export class UnitSchedulesDialogComponent {
  readonly maxSchedules = 2;
  readonly days = Object.entries(DAY_NAMES).map(([value, label]) => ({ value: Number(value), label }));
  readonly form: FormGroup;
  schedules: UnitSchedule[];
  isSaving = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly unitService: UnitService,
    private readonly notificationService: NotificationService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
    private readonly ref: MatDialogRef<UnitSchedulesDialogComponent, UnitSchedule[]>,
    @Inject(MAT_DIALOG_DATA) readonly data: UnitSchedulesDialogData,
  ) {
    this.schedules = [...data.schedules];
    this.form = this.fb.group({
      dayOfWeek: [null as number | null, Validators.required],
      startTime: ['', Validators.required],
      notes: [''],
    });
  }

  getDayName(dayOfWeek: number): string {
    return DAY_NAMES[dayOfWeek] ?? '';
  }

  addSchedule(): void {
    if (this.form.invalid || this.schedules.length >= this.maxSchedules || this.isSaving) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    if (value.dayOfWeek === null) return;
    this.isSaving = true;
    this.unitService.createSchedule(this.data.unitId, {
      dayOfWeek: value.dayOfWeek,
      startTime: value.startTime!,
      notes: value.notes || undefined,
    }).subscribe({
      next: schedule => {
        this.schedules = [...this.schedules, schedule];
        this.form.reset();
        this.isSaving = false;
        this.notificationService.success('Horario agregado correctamente.');
        this.cdr.markForCheck();
      },
      error: err => {
        this.isSaving = false;
        this.notificationService.error(err?.error?.message ?? err?.error?.title ?? 'No se pudo agregar el horario.');
        this.cdr.markForCheck();
      },
    });
  }

  deleteSchedule(schedule: UnitSchedule): void {
    const data: DeleteConfirmDialogData = {
      title: 'Eliminar horario',
      message: `¿Estás seguro de que querés eliminar el horario del ${this.getDayName(schedule.dayOfWeek)} a las ${schedule.startTime}?`,
      confirmButtonText: 'Eliminar',
    };
    this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({ data, size: 'sm', layout: 'confirm', tone: 'danger' }))
      .afterClosed().pipe(filter(result => !!result)).subscribe(() => {
        this.unitService.deleteSchedule(this.data.unitId, schedule.id).subscribe({
          next: () => {
            this.schedules = this.schedules.filter(item => item.id !== schedule.id);
            this.notificationService.success('Horario eliminado.');
            this.cdr.markForCheck();
          },
          error: () => this.notificationService.error('No se pudo eliminar el horario.'),
        });
      });
  }

  close(): void {
    this.ref.close(this.schedules);
  }
}
